import React, { useState, useMemo, useRef } from "react";
import queries from "../helpers/queries";
import { useForm } from "react-hook-form";
import { withUser } from "../Contexts/user";
import Select from "react-select";
import Table from "../Components/darkBasicTable";
import moment from "moment";
import { useQuery } from "@apollo/react-hooks";
import month from "../month";

import {
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    BarChart,
    Bar,
    ComposedChart,
} from "recharts";

const MonthlyPerformance = ({ location }) => {
    const [totalConverted, setTotalConverted] = useState(0);
    const [totalCancelled, setTotalCancelled] = useState(0);
    const [totalRejected, setTotalRejected] = useState(0);
    const [totalPending, setTotalPending] = useState(0);
    const [totalSales, setTotalSales] = useState(0);
    const [totalSalesGDV, setTotalSalesGDV] = useState(0);
    const [totalConvertedGDV, setTotalConvertedGDV] = useState(0);
    const [totalCancelledGDV, setTotalCancelledGDV] = useState(0);
    const [totalRejectedGDV, setTotalRejectedGDV] = useState(0);
    const [totalPendingGDV, setTotalPendingGDV] = useState(0);

    const [lastMonthConverted, setLastMonthConverted] = useState(0);
    const [lastMonthCancelled, setLastMonthCancelled] = useState(0);
    const [lastMonthRejected, setLastMonthRejected] = useState(0);
    const [lastMonthPending, setLastMonthPending] = useState(0);
    const [lastMonthSales, setLastMonthSales] = useState(0);
    const [periodView, setPeriodView] = useState("yearly");
    const [showComparisonChart, setShowComparisonChart] = useState(false);

    const windowWidth = useRef(window.innerWidth);

    const { errors } = useForm({
        defaultValues: {
            gallery: [{}],
            salesKit: "",
            spaPrice: "",
            rebate: "",
            fees: "",
            cashback: "",
            netPrice: "",
            status: "",
            bookingDate: moment(Date.now()).format("YYYY-MM-DD"),
            unitNumber: "",
            purchaserName: "",
            purchaserName2: "",
            purchaserName3: "",
            purchaserName4: "",
            purchaserName5: "",
            purchaserIc: "",
            purchaserIc2: "",
            purchaserIc3: "",
            purchaserIc4: "",
            purchaserIc5: "",
            purchaserContact: "",
            purchaserContact2: "",
            purchaserContact3: "",
            purchaserContact4: "",
            purchaserContact5: "",
            project: location.state
                ? location.state.project
                    ? location.state.project
                    : undefined
                : undefined,
        },
    });

    const filterSelectStyles = useMemo(
        () => ({
            control: (provided, state) => ({
                ...provided,
                borderColor: errors.area ? "#fc8181" : "#e2e8f0",
                backgroundColor: "#fbfdfe",
                borderRadius: "0.5rem",
                padding: "0.20rem",
                "&:hover": {
                    borderColor: errors.area ? "#fc8181" : "#e2e8f0",
                },
            }),
            placeholder: (provided, state) => ({
                ...provided,
                color: "#a0aec0",
                fontSize: "0.75rem",
                fontWeight: "600",
            }),
            container: (provided, state) => ({
                ...provided,
                color: "#64748b",
                fontSize: "0.75rem",
                fontWeight: "600",
                width: "100%",
            }),
        }),
        [errors]
    );

    const [paginationState, setPaginationState] = useState({
        dir: "desc",
        by: "conversionRate",
    });

    const [filters, setFilters] = useState({
        project: location.state
            ? location.state.project
                ? location.state.project
                : null
            : null,
        user: null,
        dateTo: moment(Date.now()).format("YYYY-MM-DD"),
        dateFrom: moment(Date.now()).subtract(1, "months").format("YYYY-MM-DD"),
        status: null,
        salesDirector: null,
        state: null,
        year: "2025",
        month: new Date().getMonth().toString(),
    });

    // Store previous and current month data for comparison
    const [month2Data, setMonth2Data] = useState([]);
    const [month3Data, setMonth3Data] = useState([]);

    const { data: performancesData, loading: casesLoading } = useQuery(
        queries.FETCH_MONTHLY_PERFORMANCE_AGENT,
        {
            fetchPolicy: "network-only",
            variables: {
                limit: 9999,
                page: 1,
                dir: paginationState ? paginationState.dir : undefined,
                by: paginationState ? paginationState.by : undefined,
                project: filters.project ? filters.project.value : undefined,
                user: filters.user ? filters.user : undefined,
                salesDirector: filters.salesDirector
                    ? filters.salesDirector
                    : undefined,
                year: filters.year ? filters.year : undefined,
                state: filters.state ? filters.state.value : undefined,
            },
            onCompleted: ({ fetchMonthlyPerformanceAgent: { data, totals } }) => {
                setTotalConverted(totals[0].convertedCases);
                setTotalCancelled(totals[0].cancelledCases);
                setTotalRejected(totals[0].rejectedCases);
                setTotalPending(totals[0].pendingCases);
                setTotalSales(totals[0].allCases);
                setTotalConvertedGDV(totals[0].convertNetGdv);
                setTotalCancelledGDV(totals[0].cancelledNetGdv);
                setTotalRejectedGDV(totals[0].rejectedNetGdv);
                setTotalPendingGDV(totals[0].pendingNetGdv);
                setTotalSalesGDV(totals[0].totalNetGdv);

                setLastMonthConverted(totals[1].convertedCases);
                setLastMonthCancelled(totals[1].cancelledCases);
                setLastMonthRejected(totals[1].rejectedCases);
                setLastMonthPending(totals[1].pendingCases);
                setLastMonthSales(totals[1].allCases);
                // Group data by month
          
            },
            onError: ({ graphQLErrors, networkError }) => {
                if (graphQLErrors) {
                    for (const graphQLError of graphQLErrors) {
                        console.log(graphQLError);
                    }
                }
                if (networkError) {
                    console.log(networkError);
                }
            },
        }
    );

    const columns = useMemo(() => {
        const NumberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return [
            {
                Header: "Month",
                accessor: "month",
                Cell: ({ value }) => {
                    const m = month.find((mo) => mo.value === value?.toString());
                    return <div>{m ? m.label : value}</div>;
                },
            },
            {
                Header: "Year",
                accessor: "year",
                Cell: ({ value }) => <div>{value}</div>,
            },
            {
                Header: "Sales",
                accessor: "allCases",
                Cell: ({ value }) => <div>{value}</div>,
            },
            {
                Header: "Converted",
                id: "converted",
                Cell: ({ row: { original } }) => <div>{original.convertedCases}</div>,
            },
            {
                Header: "Cancellation",
                id: "cancelled",
                Cell: ({ row: { original } }) => <div>{original.cancelledCases}</div>,
            },
            {
                Header: "Rejected",
                id: "rejected",
                Cell: ({ row: { original } }) => <div>{original.rejectedCases}</div>,
            },
            {
                Header: "Pending",
                id: "pending",
                Cell: ({ row: { original } }) => <div>{original.pendingCases}</div>,
            },
            {
                Header: "Conversion Rate",
                id: "conversionRate",
                Cell: ({ row: { original } }) => (
                    <div>{NumberFormatter.format(original.conversionRate)}%</div>
                ),
            },
            {
                Header: "Cancellation Rate",
                id: "cancellationRate",
                Cell: ({ row: { original } }) => (
                    <div>{NumberFormatter.format(original.cancellationRate)}%</div>
                ),
            },
            {
                Header: "Rejected Rate",
                id: "rejectedRate",
                Cell: ({ row: { original } }) => (
                    <div>{NumberFormatter.format(original.rejectionRate)}%</div>
                ),
            },
            {
                Header: "Pending Rate",
                id: "pendingRate",
                Cell: ({ row: { original } }) => (
                    <div>{NumberFormatter.format(original.pendingRate)}%</div>
                ),
            },
        ];
    }, []);

    // Update comparison year options to be a single year (to year)
    const comparisonYearOptions = [
        { label: "2023 - 2024", value: "2024" },
        { label: "2024 - 2025", value: "2025" },
        // Add more years as needed
    ];

    // State for selected comparison year (to year)
    const [comparisonYear, setComparisonYear] = useState(filters.year);

    // When comparisonYear changes, update filters.year
    React.useEffect(() => {
        setFilters((prev) => ({ ...prev, year: comparisonYear }));
    }, [comparisonYear]);

    // Prepare chart data for all months in both years
    const getYearComparisonChartData = () => {
        // Gather all months for both years
        const monthsArr = month.map((m) => m.label);
        const prevYear = (parseInt(comparisonYear, 10) - 1).toString();
        // Find data for each month in both years
        const data = monthsArr.map((mLabel, idx) => {
            const monthNum = (idx + 1).toString();
            const prevYearData = (performancesData && performancesData.fetchMonthlyPerformanceAgent.data.find(
                (item) => item.month === monthNum && item.year === prevYear
            )) || { allCases: 0, convertedCases: 0 };
            const currYearData = (performancesData && performancesData.fetchMonthlyPerformanceAgent.data.find(
                (item) => item.month === monthNum && item.year === comparisonYear
            )) || { allCases: 0, convertedCases: 0 };
            return {
                month: mLabel,
                [`allCases_${prevYear}`]: prevYearData.allCases,
                [`allCases_${comparisonYear}`]: currYearData.allCases,
                [`convertedCases_${prevYear}`]: prevYearData.convertedCases,
                [`convertedCases_${comparisonYear}`]: currYearData.convertedCases,
            };
        });
        return data;
    };

    // Custom number formatter for display
    const formatNumber = (num) => {
        return new Intl.NumberFormat("en-US").format(num);
    };

    const formatNumberWithCommas = (num) => {
        return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    };

    return (
        <div className="min-h-screen">
            <div className="py-6 px-4 sm:px-6 lg:px-8">
                <div
                    className="weekly-summary-card"
                    style={{
                        backgroundColor: "white",
                        borderRadius: "10px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
                        padding: "20px 25px",
                    }}
                >
                    <div
                        className="weekly-header"
                       
                    >
                        <h1
                            className="weekly-title"
                            style={{
                                fontSize: "18px",
                                fontWeight: "600",
                                color: "#333",
                            }}
                        >
                            SUMMARY &gt; COMPARISON CHART
                        </h1>
                        <div
                            className="weekly-actions"
                            style={{
                                display: "flex",
                                gap: "10px",
                            }}
                        >
                            <button
                                className={`weekly-btn ${periodView === "monthly" ? "active" : "outline"}`}
                                onClick={() => window.location.href = "/weeklyperformance"}
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    backgroundColor: periodView === "monthly" ? "#d4af37" : "transparent",
                                    color: periodView === "monthly" ? "white" : "#666",
                                    border: periodView === "monthly" ? "none" : "1px solid #ccc",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                MONTHLY
                            </button>
                            <button
                                className={`weekly-btn ${periodView === "yearly" ? "active" : "outline"}`}
                                onClick={() => setPeriodView("yearly")}
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    backgroundColor: periodView === "yearly" ? "#d4af37" : "transparent",
                                    color: periodView === "yearly" ? "white" : "#666",
                                    border: periodView === "yearly" ? "none" : "1px solid #ccc",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                YEARLY
                            </button>
                            <button
                                className={`disable-mobile weekly-btn ${showComparisonChart ? "active" : "outline"}`}
                                onClick={() => setShowComparisonChart(!showComparisonChart)}
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    backgroundColor: showComparisonChart ? "#d4af37" : "transparent",
                                    color: showComparisonChart ? "white" : "#666",
                                    border: showComparisonChart ? "none" : "1px solid #ccc",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                COMPARISON CHART
                            </button>
                            <button
                                className="weekly-btn outline export-btn"
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    backgroundColor: "#f0f0f0",
                                    color: "#666",
                                    border: "1px solid #ccc",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                }}
                            >
                                EXPORT
                            </button>
                        </div>
                    </div>
                    <div
                        className="weekly-comparison-month"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "20px 0",
                            position: "relative",
                            textAlign: "center",
                        }}
                    >
                        <>
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#555",
                                    marginRight: "10px",
                                }}
                            >
                                COMPARISON YEAR:
                            </span>
                            <select
                                className="weekly-select"
                                value={comparisonYear}
                                onChange={(e) => setComparisonYear(e.target.value)}
                                style={{
                                    padding: "8px 12px",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                    fontSize: "14px",
                                    backgroundColor: "white",
                                    minWidth: "200px",
                                }}
                            >
                                {comparisonYearOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </>
                    </div>
                    <h2
                        style={{
                            fontSize: "24px",
                            fontWeight: "700",
                            marginBottom: "20px",
                            fontFamily: "Poppins, sans-serif",
                            color: "#333",
                        }}
                    >
                     {filters.year}
                    </h2>

                    {!showComparisonChart && (
                        <div className="weekly-stats-container grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            <div
                                className={`weekly-stat-card p-4 rounded-lg shadow`}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "10px",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginBottom: "5px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "16px",
                                                fontWeight: "600",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Total Units
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "28px",
                                                fontWeight: "700",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            {(totalSales)}
                                        </p>
                        </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderTop: "1px solid #eee",
                                            paddingTop: "10px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#666",
                                                textAlign: "center",
                                                marginBottom: "5px",
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Sales GDV
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "20px",
                                                fontWeight: "700",
                                                color: "#d4af37",
                                                textAlign: "center",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            RM{" "}
                                            {formatNumberWithCommas(
                                                totalSalesGDV || 0
                                            )}
                                        </p>
                    </div>
                                </div>
                            </div>

                            <div
                                className={`weekly-stat-card p-4 rounded-lg shadow`}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "10px",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginBottom: "5px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "16px",
                                                fontWeight: "600",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Converted Units
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "28px",
                                                fontWeight: "700",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            {(
                                                totalConverted
                                            )}
                                        </p>
                        </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderTop: "1px solid #eee",
                                            paddingTop: "10px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#666",
                                                textAlign: "center",
                                                marginBottom: "5px",
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Converted GDV
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "20px",
                                                fontWeight: "700",
                                                color: "#d4af37",
                                                textAlign: "center",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            RM{" "}
                                            {formatNumberWithCommas(
                                                totalConvertedGDV || 0
                                            )}
                                        </p>
                    </div>
                </div>
                            </div>

                            <div
                                className={`weekly-stat-card p-4 rounded-lg shadow`}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "10px",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginBottom: "5px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "16px",
                                                fontWeight: "600",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Cancelled Units
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "28px",
                                                fontWeight: "700",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            {(
                                                totalCancelled
                                            )}
                                        </p>
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderTop: "1px solid #eee",
                                            paddingTop: "10px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#666",
                                                textAlign: "center",
                                                marginBottom: "5px",
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Cancelled GDV
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "20px",
                                                fontWeight: "700",
                                                color: "#d4af37",
                                                textAlign: "center",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            RM{" "}
                                            {formatNumberWithCommas(
                                                totalCancelledGDV || 0
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div
                                className={`weekly-stat-card p-4 rounded-lg shadow`}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "10px",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginBottom: "5px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "16px",
                                                fontWeight: "600",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Loan Rejected Units
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "28px",
                                                fontWeight: "700",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            {(
                                                totalRejected
                                            )}
                                        </p>
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderTop: "1px solid #eee",
                                            paddingTop: "10px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#666",
                                                textAlign: "center",
                                                marginBottom: "5px",
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Loan Rejected GDV
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "20px",
                                                fontWeight: "700",
                                                color: "#d4af37",
                                                textAlign: "center",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            RM{" "}
                                            {formatNumberWithCommas(
                                                totalRejectedGDV || 0
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div
                                className={`weekly-stat-card p-4 rounded-lg shadow`}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "10px",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginBottom: "5px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "16px",
                                                fontWeight: "600",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Pending Units
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "28px",
                                                fontWeight: "700",
                                                color: "#333",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            {(
                                                totalPending
                                            )}
                                        </p>
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderTop: "1px solid #eee",
                                            paddingTop: "10px",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#666",
                                                textAlign: "center",
                                                marginBottom: "5px",
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            Pending GDV
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "20px",
                                                fontWeight: "700",
                                                color: "#d4af37",
                                                textAlign: "center",
                                                margin: 0,
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                            }}
                                        >
                                            RM{" "}
                                            {formatNumberWithCommas(
                                                totalPendingGDV || 0
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Monthly Growth and Rate Metrics */}
                    {!showComparisonChart && (
                        <div
                            className="weekly-metrics-container"
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "3rem",
                                marginBottom: "30px",
                                justifyContent: "center",
                            }}
                        >
                            {/* Total Recruitment Section */}
                            <div
                                className="weekly-recruitment"
                                style={{
                                    textAlign: "center",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    marginRight: "15px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "200px",
                                        height: "200px",
                                        borderRadius: "50%",
                                        background: `conic-gradient(
                                    #d4af37 0% ${((totalSales / (totalSales + lastMonthSales || 1)) * 100).toFixed(2)}%,
                                    #aaa ${((totalSales / (totalSales + lastMonthSales || 1)) * 100).toFixed(2)}% 100%
                                )`,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginBottom: "5px",
                                        position: "relative",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "160px",
                                            height: "160px",
                                            borderRadius: "50%",
                                            background: "white",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            position: "absolute",
                                        }}
                                    >
                                        <p
                                            style={{
                                                fontSize: "24pt",
                                                fontWeight: "bold",
                                                color: "#d4af37",
                                                margin: 0,
                                                fontFamily: "Poppins, sans-serif",
                                            }}
                                        >
                                            {totalSales}
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "12px",
                                                color: "#666",
                                                margin: 0,
                                                fontFamily: "Poppins, sans-serif",
                                            }}
                                        >
                                            This Year
                                        </p>
                                        <div
                                            style={{
                                                width: "60%",
                                                height: "1px",
                                                background: "#eee",
                                                margin: "8px 0",
                                            }}
                                        ></div>
                                        <p
                                            style={{
                                                fontSize: "18pt",
                                                fontWeight: "bold",
                                                color: "#666",
                                                margin: 0,
                                                fontFamily: "Poppins, sans-serif",
                                            }}
                                        >
                                            {lastMonthSales}
                                        </p>
                                        <p
                                            style={{
                                                fontSize: "12px",
                                                color: "#666",
                                                margin: 0,
                                                fontFamily: "Poppins, sans-serif",
                                            }}
                                        >
                                            Last Year
                                        </p>
                                    </div>
                                </div>
                                <h3
                                    style={{
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        color: "#333",
                                        margin: "0",
                                    }}
                                >
                                    Total Sales
                                </h3>
                                <p
                                    style={{
                                        fontSize: "18pt",
                                        fontWeight: "bold",
                                        color: "#d4af37",
                                        margin: 0,
                                    }}
                                >
                                    {totalSales + lastMonthSales}
                                </p>
                            </div>
                            {/* Monthly Growth Rate */}
                            <div
                                className="weekly-growth"
                                style={{
                                    textAlign: "center",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    marginRight: "15px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "260px",
                                        height: "260px",
                                        borderRadius: "50%",
                                        background: ((totalSales - lastMonthSales) / (lastMonthSales || 1)) >= 0
                                            ? "linear-gradient(135deg, #4CAF50, #8BC34A)"
                                            : "linear-gradient(135deg, #dc3545, #ff6b6b)",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginBottom: "5px",
                                    }}
                                >
                                    <p
                                        style={{
                                            fontSize: "18px",
                                            fontWeight: "bold",
                                            color: "white",
                                            margin: 0,
                                            fontFamily: "Poppins, sans-serif",
                                        }}
                                    >
                                        Yearly<br />Growth Rate
                                    </p>
                                    <div
                                        style={{
                                            margin: "15px 0",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <svg
                                                width="50"
                                                height="25"
                                                viewBox="0 0 40 20"
                                                fill="none"
                                            >
                                                <path
                                                    d={((totalSales - lastMonthSales) / (lastMonthSales || 1)) >= 0
                                                        ? "M20 0 L40 20 L0 20 Z"
                                                        : "M0 0 L40 0 L20 20 Z"}
                                                    fill="white"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <p
                                        style={{
                                            fontSize: "36pt",
                                            fontWeight: "bold",
                                            color: "white",
                                            margin: 0,
                                            fontFamily: "Poppins, sans-serif",
                                        }}
                                    >
                                        {formatNumberWithCommas(((totalSales - lastMonthSales) / (lastMonthSales || 1)) * 100)}%
                                    </p>
                                </div>
                            </div>
                            {/* Rate Metrics */}
                            <div
                                className="weekly-rates"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, 1fr)",
                                    gap: "15px",
                                    margin: "10px 0 0 0",
                                }}
                            >
                                {/* Conversion Rate */}
                                <div className="rate-card" style={{ textAlign: "center" }}>
                                    <p style={{ fontSize: "16px", fontWeight: "600", color: "#333", margin: "0 0 2px 0" }}>Conversion Rate</p>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                                        {(() => {
                                            const diff = (((totalConverted / (totalSales || 1)) * 100) - ((lastMonthConverted / (lastMonthSales || 1)) * 100)).toFixed(2);
                                            const isPositive = parseFloat(diff) >= 0;
                                            return (
                                                <>
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d={isPositive ? "M10 0 L20 10 L15 10 L15 20 L5 20 L5 10 L0 10 Z" : "M10 20 L0 10 L5 10 L5 0 L15 0 L15 10 L20 10 Z"} fill={isPositive ? "#4CAF50" : "#dc3545"} />
                                                    </svg>
                                                    <p style={{ fontSize: "24pt", fontWeight: "bold", color: isPositive ? "#4CAF50" : "#dc3545", margin: 0, fontFamily: "Poppins, sans-serif" }}>{diff}%</p>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                {/* Cancel Rate */}
                                <div className="rate-card" style={{ textAlign: "center" }}>
                                    <p style={{ fontSize: "16px", fontWeight: "600", color: "#333", margin: "0 0 2px 0" }}>Cancel Rate</p>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                                        {(() => {
                                            const diff = (((totalCancelled / (totalSales || 1)) * 100) - ((lastMonthCancelled / (lastMonthSales || 1)) * 100)).toFixed(2);
                                            const isPositive = parseFloat(diff) >= 0;
                                            return (
                                                <>
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d={isPositive ? "M10 0 L20 10 L15 10 L15 20 L5 20 L5 10 L0 10 Z" : "M10 20 L0 10 L5 10 L5 0 L15 0 L15 10 L20 10 Z"} fill={isPositive ? "#4CAF50" : "#dc3545"} />
                                                    </svg>
                                                    <p style={{ fontSize: "24pt", fontWeight: "bold", color: isPositive ? "#4CAF50" : "#dc3545", margin: 0, fontFamily: "Poppins, sans-serif" }}>{diff}%</p>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                {/* Loan Reject Rate */}
                                <div className="rate-card" style={{ textAlign: "center" }}>
                                    <p style={{ fontSize: "16px", fontWeight: "600", color: "#333", margin: "0 0 2px 0" }}>Loan Reject Rate</p>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                                        {(() => {
                                            const diff = (((totalRejected / (totalSales || 1)) * 100) - ((lastMonthRejected / (lastMonthSales || 1)) * 100)).toFixed(2);
                                            const isPositive = parseFloat(diff) >= 0;
                                            return (
                                                <>
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d={isPositive ? "M10 0 L20 10 L15 10 L15 20 L5 20 L5 10 L0 10 Z" : "M10 20 L0 10 L5 10 L5 0 L15 0 L15 10 L20 10 Z"} fill={isPositive ? "#4CAF50" : "#dc3545"} />
                                                    </svg>
                                                    <p style={{ fontSize: "24pt", fontWeight: "bold", color: isPositive ? "#4CAF50" : "#dc3545", margin: 0, fontFamily: "Poppins, sans-serif" }}>{diff}%</p>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                {/* Pending Rate */}
                                <div className="rate-card" style={{ textAlign: "center" }}>
                                    <p style={{ fontSize: "16px", fontWeight: "600", color: "#333", margin: "0 0 2px 0" }}>Pending Rate</p>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                                        {(() => {
                                            const diff = (((totalPending / (totalSales || 1)) * 100) - ((lastMonthPending / (lastMonthSales || 1)) * 100)).toFixed(2);
                                            const isPositive = parseFloat(diff) >= 0;
                                            return (
                                                <>
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d={isPositive ? "M10 0 L20 10 L15 10 L15 20 L5 20 L5 10 L0 10 Z" : "M10 20 L0 10 L5 10 L5 0 L15 0 L15 10 L20 10 Z"} fill={isPositive ? "#4CAF50" : "#dc3545"} />
                                                    </svg>
                                                    <p style={{ fontSize: "24pt", fontWeight: "bold", color: isPositive ? "#4CAF50" : "#dc3545", margin: 0, fontFamily: "Poppins, sans-serif" }}>{diff}%</p>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Comparison Chart Section */}
                    {showComparisonChart && (
                        <div
                            className="weekly-comparison-section"
                            style={{
                                marginBottom: "30px",
                                backgroundColor: "white",
                                borderRadius: "8px",
                                overflow: "hidden",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
                            }}
                        >
                            <div
                                className="weekly-comparison-header"
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "15px 20px",
                                    borderBottom: "1px solid #eee",
                                }}
                            >
                                <h3
                                    className="weekly-comparison-title"
                                    style={{
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        color: "#333",
                                        margin: 0,
                                    }}
                                >
                                    Sales
                                </h3>
                            </div>
                            <div
                                className="weekly-chart-container"
                                style={{
                                    padding: "20px",
                                    backgroundColor: "white",
                                }}
                            >
                                <ComposedChart
                                    width={windowWidth.current - 150}
                                    height={500}
                                    data={getYearComparisonChartData()}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 40,
                                        bottom: 45,
                                    }}
                                    barSize={40}
                                    maxBarSize={50}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" height={60} label={{ value: "Month", position: "insideBottom", offset: -35 }} />
                                    <YAxis domain={[0, "dataMax + 50"]} />
                                    <Tooltip />
                                    <Legend
                                        content={() => {
                                            const prevYear = (parseInt(comparisonYear, 10) - 1).toString();
                                            return (
                                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px", padding: "10px 0" }}>
                                                    <div style={{ display: "flex", alignItems: "center", marginRight: "20px" }}>
                                                        <div style={{ width: "12px", height: "12px", backgroundColor: "#dc3545", marginRight: "5px" }}></div>
                                                        <span style={{ fontSize: "12px", color: "#666" }}>{prevYear}</span>
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <div style={{ width: "12px", height: "12px", backgroundColor: "#d4af37", marginRight: "5px" }}></div>
                                                        <span style={{ fontSize: "12px", color: "#666" }}>{comparisonYear}</span>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                    <Bar dataKey={`allCases_${comparisonYear}`} fill="#d4af37" name={comparisonYear} radius={[5, 5, 0, 0]} />
                                    <Line type="monotone" dataKey={`allCases_${(parseInt(comparisonYear, 10) - 1).toString()}`} stroke="#dc3545" strokeWidth={2} dot={{ fill: "#dc3545", r: 5 }} activeDot={{ r: 7 }} name={(parseInt(comparisonYear, 10) - 1).toString()} />
                                </ComposedChart>
                            </div>
                        </div>
                    )}

                    {showComparisonChart && (
                        <div
                            className="weekly-comparison-section"
                            style={{
                                marginBottom: "30px",
                                backgroundColor: "white",
                                borderRadius: "8px",
                                overflow: "hidden",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
                            }}
                        >
                            <div
                                className="weekly-comparison-header"
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "15px 20px",
                                    borderBottom: "1px solid #eee",
                                }}
                            >
                                <h3
                                    className="weekly-comparison-title"
                                    style={{
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        color: "#333",
                                        margin: 0,
                                    }}
                                >
                                    Converted
                                </h3>
                            </div>
                            <div
                                className="weekly-chart-container"
                                style={{
                                    padding: "20px",
                                    backgroundColor: "white",
                                }}
                            >
                                <ComposedChart
                                    width={windowWidth.current - 150}
                                    height={500}
                                    data={getYearComparisonChartData()}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 40,
                                        bottom: 45,
                                    }}
                                    barSize={40}
                                    maxBarSize={50}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" height={60} label={{ value: "Month", position: "insideBottom", offset: -35 }} />
                                    <YAxis domain={[0, "dataMax + 50"]} />
                                    <Tooltip />
                                    <Legend
                                        content={() => {
                                            const prevYear = (parseInt(comparisonYear, 10) - 1).toString();
                                            return (
                                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px", padding: "10px 0" }}>
                                                    <div style={{ display: "flex", alignItems: "center", marginRight: "20px" }}>
                                                        <div style={{ width: "12px", height: "12px", backgroundColor: "#dc3545", marginRight: "5px" }}></div>
                                                        <span style={{ fontSize: "12px", color: "#666" }}>{prevYear}</span>
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <div style={{ width: "12px", height: "12px", backgroundColor: "#d4af37", marginRight: "5px" }}></div>
                                                        <span style={{ fontSize: "12px", color: "#666" }}>{comparisonYear}</span>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                    <Bar dataKey={`convertedCases_${comparisonYear}`} fill="#d4af37" name={comparisonYear} radius={[5, 5, 0, 0]} />
                                    <Line type="monotone" dataKey={`convertedCases_${(parseInt(comparisonYear, 10) - 1).toString()}`} stroke="#dc3545" strokeWidth={2} dot={{ fill: "#dc3545", r: 5 }} activeDot={{ r: 7 }} name={(parseInt(comparisonYear, 10) - 1).toString()} />
                                </ComposedChart>
                            </div>
                        </div>
                    )}

                    {showComparisonChart && (
                        <div
                            className="weekly-table-container mt-8"
                            style={{ marginTop: "30px" }}
                        >
                            <div
                                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}
                            >
                                <div style={{ display: "flex", gap: "8px" }}>
                                    <Select
                                        styles={filterSelectStyles}
                                        isSearchable={true}
                                        options={month}
                                        placeholder="Month"
                                        value={month.find((m) => m.value === filters.month) || null}
                                        onChange={(val) =>
                                            setFilters((prev) => ({
                                                ...prev,
                                                month: val.value,
                                            }))
                                        }
                                        className="w-40"
                                    />
                                    <input
                                        type="number"
                                        className="weekly-select"
                                        placeholder="Year"
                                        value={filters.year}
                                        onChange={(e) =>
                                            setFilters((prev) => ({
                                                ...prev,
                                                year: e.target.value,
                                            }))
                                        }
                                        style={{
                                            padding: "8px 12px",
                                            borderRadius: "4px",
                                            border: "1px solid #ccc",
                                            fontSize: "14px",
                                            width: "100px",
                                        }}
                                    />
                                </div>
                                <button
                                    className="weekly-btn"
                                    style={{
                                        padding: "8px 16px",
                                        borderRadius: "4px",
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        backgroundColor: "#d4af37",
                                        color: "white",
                                        border: "none",
                                        cursor: "pointer",
                                    }}
                                >
                                    EXPORT DATA
                                </button>
                            </div>
                            <Table
                                columns={columns}
                                data={
                                    performancesData
                                        ? performancesData.fetchMonthlyPerformanceAgent.data
                                        : []
                                }
                                updatePagination={setPaginationState}
                                paginationState={paginationState}
                                loading={casesLoading}
                                pageCount={0}
                                totalCount={0}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default withUser(MonthlyPerformance);
